import { useNavigate } from 'react-router-dom';
import { Bug, Eye, Smartphone } from 'lucide-react';
import { Modal } from '@/components/common/Modal';
import { GradeNumeros, IconeQuadrado } from '@/components/common/ListaMobile';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/formatters';
import { nomePlataforma, rotuloVersao } from '@/utils/aparelho';
import type { Device } from '@/types/device.types';

// Peças da lista de aparelhos no celular (Dispositivos e Logs).

// Ícone de celular no quadrado com a bolinha de status no canto, como no
// desenho aprovado. O IconeQuadrado comum não tem a bolinha; ela fica aqui.
export function IconeAparelho({ online }: { online: boolean }) {
  return (
    <span className="relative flex">
      <IconeQuadrado>
        <Smartphone size={18} aria-hidden="true" />
      </IconeQuadrado>
      <span
        className={cn(
          'absolute -bottom-0.5 -right-0.5 size-3 rounded-full ring-2 ring-card',
          online ? 'bg-online' : 'bg-offline'
        )}
        aria-label={online ? 'Online' : 'Offline'}
      />
    </span>
  );
}

// Detalhe do aparelho tocado na lista: no celular o card grande não cabe na
// linha, então sessões, erros, crashes e os atalhos de Logs e Erros ficam
// nesta folha (o Modal abre por baixo no celular).
export function DetalheAparelhoModal({
  device,
  onClose,
}: {
  device: Device | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  if (!device) return null;

  const online = device.status === 'online';
  const sistema = [nomePlataforma(device.platform), device.osVersion].filter(Boolean).join(' ');
  const linhas = [
    { rotulo: 'Dono', valor: device.userName },
    { rotulo: 'Sistema', valor: sistema },
    { rotulo: 'App', valor: rotuloVersao(device.appVersion) },
    { rotulo: 'Última atividade', valor: formatRelativeTime(device.lastActivity) },
  ];

  return (
    <Modal isOpen onClose={onClose} title={device.model} size="sm">
      <div className="flex flex-col gap-4 p-4">
        <span
          className={cn(
            'inline-flex w-fit items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
            online ? 'bg-online/10 text-online' : 'bg-offline/10 text-offline'
          )}
        >
          <span className="size-1.5 rounded-full bg-current" />
          {online ? 'Online' : 'Offline'}
        </span>

        <dl className="flex flex-col gap-2 text-sm">
          {linhas.map((l) => (
            <div key={l.rotulo} className="flex items-baseline justify-between gap-3">
              <dt className="shrink-0 text-text-secondary">{l.rotulo}</dt>
              <dd className="min-w-0 truncate text-right font-medium text-text-title">
                {l.valor || 'Não informado'}
              </dd>
            </div>
          ))}
          <div className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-text-secondary">ID</dt>
            <dd className="min-w-0 truncate text-right font-mono text-xs text-text-body">
              {device.id}
            </dd>
          </div>
        </dl>

        <GradeNumeros
          itens={[
            { rotulo: 'Sessões', valor: (device.sessions || 0).toLocaleString('pt-BR') },
            {
              rotulo: 'Erros',
              valor: device.errors || 0,
              cor: device.errors > 0 ? 'text-warning' : undefined,
            },
            {
              rotulo: 'Crashes',
              valor: device.crashes || 0,
              cor: device.crashes > 0 ? 'text-error' : undefined,
            },
          ]}
        />

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => navigate(`/logs?device=${device.id}`)}
            className="flex h-11 items-center justify-center gap-2 rounded-lg border border-border-strong bg-card text-sm font-semibold text-text-title transition-colors active:bg-muted"
          >
            <Eye size={16} /> Logs
          </button>
          <button
            type="button"
            onClick={() => navigate(`/errors?device=${device.id}`)}
            className="flex h-11 items-center justify-center gap-2 rounded-lg border border-border-strong bg-card text-sm font-semibold text-text-title transition-colors active:bg-muted"
          >
            <Bug size={16} /> Erros
          </button>
        </div>
      </div>
    </Modal>
  );
}
